import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Lightweight i18n. We translate the shell (nav labels, page titles, common
 * buttons) — not every page string. Selected locale persists in localStorage.
 *
 * Add a language: append to LOCALES + fill in the same keys in dictionary.
 */

export const LOCALES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi",   native: "\u0939\u093f\u0902\u0926\u0940" },        // हिंदी
  { code: "kn", label: "Kannada", native: "\u0c95\u0ca8\u0ccd\u0ca8\u0ca1" },        // ಕನ್ನಡ
  { code: "mr", label: "Marathi", native: "\u092e\u0930\u093e\u0920\u0940" },        // मराठी
];

const dict = {
  // Shell navigation
  "nav.home": {
    en: "Home",
    hi: "\u092e\u0941\u0916\u094d\u092f",            // मुख्य
    kn: "\u0cae\u0cc1\u0c96\u0caa\u0cc1\u0c9f",      // ಮುಖಪುಟ
    mr: "\u092e\u0941\u0916\u094d\u092f\u092a\u0943\u0937\u094d\u0920",  // मुख्यपृष्ठ
  },
  "nav.dashboard": {
    en: "Dashboard",
    hi: "\u0921\u0948\u0936\u092c\u094b\u0930\u094d\u0921",       // डैशबोर्ड
    kn: "\u0ca1\u0ccd\u0caf\u0cbe\u0cb6\u0ccd\u0cac\u0ccb\u0cb0\u0ccd\u0ca1\u0ccd",
    mr: "\u0921\u0945\u0936\u092c\u094b\u0930\u094d\u0921",
  },
  "nav.leads": {
    en: "Lead Room",
    hi: "\u0932\u0940\u0921 \u0915\u0915\u094d\u0937",            // लीड कक्ष
    kn: "\u0cb8\u0cc1\u0cb3\u0cbf\u0cb5\u0cc1 \u0c95\u0cca\u0ca0\u0ca1\u0cbf", // ಸುಳಿವು ಕೊಠಡಿ
    mr: "\u0938\u0902\u0915\u0947\u0924 \u0915\u0915\u094d\u0937",             // संकेत कक्ष
  },
  "nav.ads": {
    en: "Advertisements",
    hi: "\u0935\u093f\u091c\u094d\u091e\u093e\u092a\u0928",       // विज्ञापन
    kn: "\u091c\u093e\u0939\u0940\u0930\u093e\u0924\u0940",
    mr: "\u091c\u093e\u0939\u093f\u0930\u093e\u0924\u0940",
  },
  "nav.networks": {
    en: "Networks",
    hi: "\u0928\u0947\u091f\u0935\u0930\u094d\u0915",             // नेटवर्क
    kn: "\u0ca8\u0cc6\u0c9f\u0ccd\u200c\u0cb5\u0cb0\u0ccd\u0c95\u0ccd\u200c\u0c97\u0cb3\u0cc1",
    mr: "\u0928\u0947\u091f\u0935\u0930\u094d\u0915",
  },
  "nav.intel": {
    en: "Intelligence Graph",
    hi: "\u0916\u0941\u092b\u093f\u092f\u093e \u0906\u0932\u0947\u0916",  // खुफिया आलेख
    kn: "\u0c97\u0cc1\u0caa\u0ccd\u0ca4\u0c9a\u0cbe\u0cb0 \u0c9a\u0cbf\u0ca4\u0ccd\u0cb0",
    mr: "\u0917\u0941\u092a\u094d\u0924\u091a\u0930 \u0906\u0932\u0947\u0916",
  },
  "nav.geo": {
    en: "Geographic",
    hi: "\u092d\u094c\u0917\u094b\u0932\u093f\u0915",             // भौगोलिक
    kn: "\u0cad\u0ccc\u0c97\u0ccb\u0cb2\u0cbf\u0c95",
    mr: "\u092d\u094c\u0917\u094b\u0932\u093f\u0915",
  },
  "nav.search": {
    en: "Search",
    hi: "\u0916\u094b\u091c",                                      // खोज
    kn: "\u0cb9\u0cc1\u0ca1\u0cc1\u0c95\u0cbe\u0c9f",
    mr: "\u0936\u094b\u0927",
  },
  "nav.digest": {
    en: "Digest",
    hi: "\u0938\u093e\u0930\u093e\u0902\u0936",                    // सारांश
    kn: "\u0cb8\u0cbe\u0cb0\u0cbe\u0c82\u0cb6",
    mr: "\u0938\u093e\u0930\u093e\u0902\u0936",
  },
  "nav.cases": {
    en: "Cases",
    hi: "\u092e\u093e\u092e\u0932\u0947",                          // मामले
    kn: "\u0c95\u0cc7\u0cb8\u0cc1\u0c97\u0cb3\u0cc1",
    mr: "\u092a\u094d\u0930\u0915\u0930\u0923\u0947",
  },
  "nav.crawler": {
    en: "Crawler",
    hi: "\u0915\u094d\u0930\u0949\u0932\u0930",                    // क्रॉलर
    kn: "\u0c95\u0ccd\u0cb0\u0cbe\u0cb2\u0cb0\u0ccd",
    mr: "\u0915\u094d\u0930\u0949\u0932\u0930",
  },
  "nav.alerts": {
    en: "Alerts",
    hi: "\u091a\u0947\u0924\u093e\u0935\u0928\u093f\u092f\u093e\u0901",  // चेतावनियाँ
    kn: "\u0c8e\u0c9a\u0ccd\u0c9a\u0cb0\u0cbf\u0c95\u0cc6\u0c97\u0cb3\u0cc1",
    mr: "\u0907\u0936\u093e\u0930\u0947",
  },
  "nav.assistant": {
    en: "AI Assistant",
    hi: "\u090f\u0906\u0908 \u0938\u0939\u093e\u092f\u0915",                    // एआई सहायक
    kn: "\u0c8e\u0c90 \u0cb8\u0cb9\u0cbe\u0caf\u0c95",                           // ಎಐ ಸಹಾಯಕ
    mr: "\u090f\u0906\u092f \u0938\u0939\u093e\u092f\u094d\u092f\u0915",         // एआय सहाय्यक
  },
  "nav.networkExplorer": {
    en: "Network explorer",
    hi: "\u0928\u0947\u091f\u0935\u0930\u094d\u0915 \u0905\u0928\u094d\u0935\u0947\u0937\u0915",  // नेटवर्क अन्वेषक
    kn: "\u0ca8\u0cc6\u0c9f\u0ccd\u0cb5\u0cb0\u094d\u0915 \u0c85\u0ca8\u094d\u0cb5\u0cc7\u0cb7\u0c95",
    mr: "\u0928\u0947\u091f\u0935\u0930\u094d\u0915 \u0905\u0928\u094d\u0935\u0947\u0937\u0915",
  },
  "nav.phoneLookup": {
    en: "Phone lookup",
    hi: "\u092b\u094b\u0928 \u0916\u094b\u091c",                                  // फोन खोज
    kn: "\u092b\u094b\u0928\u094d \u0cb9\u0cc1\u0ca1\u0cc1\u0c95\u0cbe\u091f",     // ಫೋನ್ ಹುಡುಕಾಟ
    mr: "\u092b\u094b\u0928 \u0936\u094b\u0927",
  },
  "nav.imageClusters": {
    en: "Image clusters",
    hi: "\u091a\u093f\u0924\u094d\u0930 \u0938\u092e\u0942\u0939",               // चित्र समूह
    kn: "\u091a\u093f\u0924\u094d\u0930 \u0917\u0941\u091a\u094d\u091b\u0917\u0933\u0941",  // ಚಿತ್ರ ಗುಚ್ಛಗಳು
    mr: "\u091a\u093f\u0924\u094d\u0930 \u0938\u092e\u0942\u0939",
  },
  "nav.relationshipGraph": {
    en: "Relationship graph",
    hi: "\u0938\u0902\u092c\u0902\u0927 \u0906\u0932\u0947\u0916",                // संबंध आलेख
    kn: "\u0cb8\u0c82\u092c\u0902\u0927 \u0906\u0932\u0947\u0916",                // ಸಂಬಂಧ ಆಲೇಖ
    mr: "\u0938\u0902\u092c\u0902\u0927 \u0906\u0932\u0947\u0916",
  },
  "nav.timeline": {
    en: "Timeline",
    hi: "\u0938\u092e\u092f\u0930\u0947\u0916\u093e",                              // समयरेखा
    kn: "\u0938\u092e\u092f\u0938\u0942\u091a\u0940",                              // ಸಮಯಸೂಚಿ (will convert)
    mr: "\u0915\u093e\u0932\u0930\u0947\u0937\u093e",
  },
  "nav.fieldCapture": {
    en: "Field capture",
    hi: "\u0915\u094d\u0937\u0947\u0924\u094d\u0930 \u0915\u0948\u092a\u094d\u091a\u0930",  // क्षेत्र कैप्चर
    kn: "\u0c95\u094d\u0937\u0947\u0924\u094d\u0930 \u0938\u0902\u0917\u094d\u0930\u0939",
    mr: "\u0915\u094d\u0937\u0947\u0924\u094d\u0930 \u0938\u0902\u0917\u094d\u0930\u0939",
  },
  "nav.users": {
    en: "Users & roles",
    hi: "\u0909\u092a\u092f\u094b\u0917\u0915\u0930\u094d\u0924\u093e \u0914\u0930 \u092d\u0942\u092e\u093f\u0915\u093e\u090f\u0902",  // उपयोगकर्ता और भूमिकाएं
    kn: "\u0cac\u0cb3\u0c95\u0cc6\u0ca6\u0cbe\u0cb0\u0cb0\u0cc1 \u092e\u0924\u094d\u0924\u0941 \u092a\u093e\u0924\u094d\u0930\u0917\u0933\u0941",  // ಬಳಕೆದಾರರು ಮತ್ತು ಪಾತ್ರಗಳು
    mr: "\u0935\u093e\u092a\u0930\u0915\u0930\u094d\u0924\u0947 \u0906\u0923\u093f \u092d\u0942\u092e\u093f\u0915\u093e",
  },
  "nav.policeRoster": {
    en: "Police roster",
    hi: "\u092a\u0941\u0932\u093f\u0938 \u0938\u0942\u091a\u0940",                // पुलिस सूची
    kn: "\u092a\u094b\u0932\u0940\u0938\u094d \u0cb8\u0cc2\u091a\u093f",            // ಪೋಲೀಸ್ ಸೂಚಿ
    mr: "\u092a\u094b\u0932\u0940\u0938 \u0938\u0942\u091a\u0940",
  },
  "nav.citizenReports": {
    en: "Citizen reports",
    hi: "\u0928\u093e\u0917\u0930\u093f\u0915 \u0930\u093f\u092a\u094b\u0930\u094d\u091f",  // नागरिक रिपोर्ट
    kn: "\u092a\u094c\u0930 \u0935\u0930\u0926\u093f",                              // ಪೌರ ವರದಿ
    mr: "\u0928\u093e\u0917\u0930\u093f\u0915 \u0905\u0939\u0935\u093e\u0932",       // नागरिक अहवाल
  },
  "nav.admin": {
    en: "Admin",
    hi: "\u092a\u094d\u0930\u092c\u0902\u0927\u0915",                               // प्रबंधक
    kn: "\u0ca8\u093f\u0930\u094d\u0935\u093e\u0939\u0915",                         // ನಿರ್ವಾಹಕ
    mr: "\u092a\u094d\u0930\u0936\u093e\u0938\u0915",                               // प्रशासक
  },

  // Home / MuktiHome page
  "home.watchDelta": {
    en: "Since you were last here",
    hi: "\u091c\u092c \u0906\u092a \u0905\u0902\u0924\u093f\u092e \u092c\u093e\u0930 \u092f\u0939\u093e\u0901 \u0925\u0947",  // जब आप अंतिम बार यहाँ थे
    kn: "\u0ca8\u0cc0\u0cb5\u0cc1 \u0c95\u0cca\u0ca8\u0cc6\u092f \u092c\u093e\u0930\u093f \u092c\u0902\u0926 \u0928\u0902\u0924\u0930",
    mr: "\u0924\u0941\u092e\u094d\u0939\u0940 \u0936\u0947\u0935\u091f\u091a\u094d\u092f\u093e\u0902\u0926\u093e \u0906\u0932\u094d\u092f\u093e\u092a\u093e\u0938\u0942\u0928",
  },
  "home.openInvestigate": {
    en: "Open investigation →",
    hi: "\u091c\u093e\u0902\u091a \u0916\u094b\u0932\u0947\u0902 \u2192",            // जांच खोलें →
    kn: "\u0ca4\u0ca8\u0cbf\u0c96\u0cc6 \u0ca4\u0cc6\u0cb0\u0cc6\u092f\u093f\u0930\u0940 \u2192",
    mr: "\u0924\u092a\u093e\u0938 \u0909\u0918\u0921\u093e \u2192",                 // तपास उघडा →
  },
  "common.loading": {
    en: "Loading…",
    hi: "\u0932\u094b\u0921 \u0939\u094b \u0930\u0939\u093e \u0939\u0948…",         // लोड हो रहा है…
    kn: "\u0ca8\u0cad\u0940 \u092e\u093e\u0921\u0932\u093e\u0917\u0941\u0924\u094d\u0924\u093f\u0926\u0947…",
    mr: "\u0932\u094b\u0921 \u0939\u094b\u0924 \u0906\u0939\u0947…",
  },
  // Lead Room
  "leads.title": {
    en: "Lead Room",
    hi: "\u0932\u0940\u0921 \u0915\u0915\u094d\u0937",            // लीड कक्ष
    kn: "\u0cb8\u0cc1\u0cb3\u0cbf\u0cb5\u0cc1 \u0c95\u0cca\u0ca0\u0ca1\u0cbf",
    mr: "\u0938\u0902\u0915\u0947\u0924 \u0915\u0915\u094d\u0937",
  },
  "leads.subtitle": {
    en: "Every lead is a cluster of knowledge from multiple sources, not a single ad. Open one to see what investigators already know and what still needs human review.",
    hi: "\u092a\u094d\u0930\u0924\u094d\u092f\u0947\u0915 \u0932\u0940\u0921 \u090f\u0915 \u0938\u094d\u0924\u094d\u0930\u094b\u0924 \u0928\u0939\u0940\u0902, \u0915\u0908 \u0938\u094d\u0924\u094d\u0930\u094b\u0924\u094b\u0902 \u0938\u0947 \u091c\u0941\u0921\u093c\u0940 \u091c\u093e\u0928\u0915\u093e\u0930\u0940 \u0915\u093e \u0938\u092e\u0942\u0939 \u0939\u0948\u0964 \u091c\u093e\u0902\u091a\u0915\u0930\u094d\u0924\u093e\u0913\u0902 \u0915\u094b \u0915\u094d\u092f\u093e \u092a\u0924\u093e \u0939\u0948 \u0914\u0930 \u0915\u094d\u092f\u093e \u0906\u0917\u0947 \u0938\u092e\u0940\u0915\u094d\u0937\u093e \u091a\u093e\u0939\u093f\u090f, \u092f\u0939 \u0926\u0947\u0916\u0928\u0947 \u0915\u0947 \u0932\u093f\u090f \u0915\u093f\u0938\u0940 \u090f\u0915 \u0915\u094b \u0916\u094b\u0932\u0947\u0902\u0964",
    kn: "\u092a\u094d\u0930\u0924\u093f \u0938\u0941\u0933\u093f\u0935\u0941 \u0906 \u0917\u0941\u091a\u094d\u091b\u0940\u092f \u091c\u094d\u091e\u093e\u0928\u0926 \u0938\u0902\u0917\u094d\u0930\u0939\u0935\u093e\u0917\u093f\u0926\u0947. \u092e\u0941\u0902\u091a\u0942\u0923\u093f \u0924\u0928\u093f\u0916\u0947\u0917\u0941 \u0937\u0940\u0928\u093f\u092f\u0930\u094d \u0935\u093f\u0935\u0930\u0923\u0917\u094b\u0938\u094d\u0915\u0930\u093f \u0924\u0947\u0930\u0947\u092f\u093f\u0930\u093f.",
    mr: "\u092a\u094d\u0930\u0924\u094d\u092f\u0947\u0915 \u0938\u0902\u0915\u0947\u0924 \u0935\u0947\u0917\u0935\u0947\u0917\u0933\u094d\u092f\u093e \u0938\u094d\u0924\u094d\u0930\u094b\u0924\u093e\u0902\u0924\u0942\u0928 \u0924\u092f\u093e\u0930 \u091d\u093e\u0932\u0947\u0932\u093e \u091c\u094d\u091e\u093e\u0928\u093e\u091a\u093e \u091a\u094b\u0919\u093e \u0906\u0939\u0947. \u0924\u092a\u093e\u0938\u0915\u0930\u094d\u0924\u094d\u092f\u093e\u0902\u091a\u094d\u092f\u093e \u092a\u093e\u0923\u094d\u092f\u093e\u0924 \u0906\u0932\u0947\u0932\u0947 \u0906\u0923\u093f \u092a\u0941\u0922\u0940\u0932 \u0924\u092a\u093e\u0938\u093e\u0938\u093e\u0920\u0940 \u0909\u0927\u094d\u0918\u094b\u0938\u0923\u093e \u0905\u0938\u0932\u0947\u0932\u0947 \u092a\u093e\u0939\u0923\u094d\u092f\u093e\u0938\u093e\u0920\u0940 \u090f\u0915 \u0938\u0902\u0915\u0947\u0924 \u0909\u0918\u0921\u093e.",
  },
  "leads.rebuild": {
    en: "Rebuild leads",
    hi: "\u0932\u0940\u0921 \u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923",  // लीड पुनःनिर्माण
    kn: "\u0cb8\u0cc1\u0cb3\u0cbf\u0cb5\u0cc1 \u092e\u0930\u0941 \u0928\u093f\u0930\u094d\u092e\u093e\u0923",
    mr: "\u0938\u0902\u0915\u0947\u0924 \u092a\u0941\u0928\u0930\u094d\u0935\u093f\u0915\u093e\u0938",
  },
  "leads.openLead": {
    en: "Open lead →",
    hi: "\u0932\u0940\u0921 \u0916\u094b\u0932\u0947\u0902 \u2192",
    kn: "\u0cb8\u0cc1\u0cb3\u0cbf\u0cb5\u0cc1 \u0ca4\u0cc6\u0cb0\u0cc6\u092f\u093f \u2192",
    mr: "\u0938\u0902\u0915\u0947\u0924 \u0909\u0918\u0921\u093e \u2192",
  },
  // Splash / landing
  "splash.cta.enter": {
    en: "Enter MUKTI",
    hi: "\u092e\u0941\u0915\u094d\u0924\u093f \u092e\u0947\u0902 \u092a\u094d\u0930\u0935\u0947\u0936",   // मुक्ति में प्रवेश
    kn: "\u092e\u0941\u0915\u094d\u0924\u093f \u092a\u094d\u0930\u0935\u0947\u0936\u093f\u0938\u093f",     // ಮುಕ್ತಿ ಪ್ರವೇಶಿಸಿ
    mr: "\u092e\u0941\u0915\u094d\u0924\u0940\u092e\u0927\u094d\u092f\u0947 \u092a\u094d\u0930\u0935\u0947\u0936",
  },
  "splash.investigativeUse": {
    en: "Investigative use only",
    hi: "\u0915\u0947\u0935\u0932 \u091c\u093e\u0902\u091a \u0909\u092a\u092f\u094b\u0917 \u0939\u0947\u0924\u0941",   // केवल जांच उपयोग हेतु
    kn: "\u0ca4\u0ca8\u093f\u0916\u0947\u0917\u093e\u0917\u0940 \u092e\u093e\u0924\u094d\u0930",             // ತನಿಖೆಗಾಗಿ ಮಾತ್ರ
    mr: "\u092b\u0915\u094d\u0924 \u0924\u092a\u093e\u0938\u0923\u0940\u0938\u093e\u0920\u0940",             // फक्त तपासणीसाठी
  },
  "splash.citizenCta": {
    en: "Are you a citizen with a concern?",
    hi: "\u0915\u094d\u092f\u093e \u0906\u092a\u0915\u094b \u0915\u0941\u091b \u0938\u0902\u0926\u0947\u0939\u093e\u0938\u094d\u092a\u0926 \u0926\u093f\u0916\u093e?",  // क्या आपको कुछ संदेहास्पद दिखा?
    kn: "\u0ca8\u0cbf\u092e\u094d\u092e \u0938\u0902\u0936\u092f \u0907\u0926\u0947\u092f\u0947?",       // ನಿಮ್ಮ ಸಂಶಯ ಇದೆಯೇ?
    mr: "\u0924\u0941\u092e\u094d\u0939\u093e\u0932\u093e \u0915\u093e\u0939\u0940 \u0938\u0902\u0936\u092f\u093e\u0938\u094d\u092a\u0926 \u0906\u0922\u0933\u0947?",  // तुम्हाला काही संशयास्पद आढळले?
  },
  "splash.reportAnon": {
    en: "Report anonymously →",
    hi: "\u0917\u0941\u092a\u094d\u0924 \u0930\u0942\u092a \u0938\u0947 \u0930\u093f\u092a\u094b\u0930\u094d\u091f \u0915\u0930\u0947\u0902 \u2192",   // गुप्त रूप से रिपोर्ट करें →
    kn: "\u0c97\u0cc1\u092a\u094d\u0924\u0935\u093e\u0917\u093f \u0935\u0930\u0926\u093f \u092e\u093e\u0921\u093f \u2192",                 // ಗುಪ್ತವಾಗಿ ವರದಿ ಮಾಡಿ →
    mr: "\u0917\u0941\u092a\u094d\u0924 \u0905\u0939\u0935\u093e\u0932 \u0926\u094d\u092f\u093e \u2192",                             // गुप्त अहवाल द्या →
  },
  "nav.audit": {
    en: "Audit Log",
    hi: "\u0911\u0921\u093f\u091f \u0932\u0949\u0917",             // ऑडिट लॉग
    kn: "\u0c86\u0ca1\u0cbf\u0c9f\u0ccd \u0cb2\u0cbe\u0c97\u0ccd",
    mr: "\u0911\u0921\u093f\u091f \u0932\u0949\u0917",
  },
  "common.language": {
    en: "Language",
    hi: "\u092d\u093e\u0937\u093e",                                // भाषा
    kn: "\u0cad\u0cbe\u0cb7\u0cc6",
    mr: "\u092d\u093e\u0937\u093e",
  },
  "common.signOut": {
    en: "Sign out",
    hi: "\u0938\u093e\u0907\u0928 \u0906\u0909\u091f",             // साइन आउट
    kn: "\u0cb8\u0cc8\u0ca8\u0ccd \u0c94\u0c9f\u0ccd",
    mr: "\u0938\u093e\u0907\u0928 \u0906\u0909\u091f",
  },
  "common.welcome": {
    en: "Welcome",
    hi: "\u0938\u094d\u0935\u093e\u0917\u0924",                    // स्वागत
    kn: "\u0cb8\u0ccd\u0cb5\u0cbe\u0c97\u0ca4",
    mr: "\u0938\u094d\u0935\u093e\u0917\u0924",
  },
  // MUKTI 2.0 — Home hero
  "home.question": {
    en: "What do you want to investigate?",
    hi: "\u0906\u092a \u0915\u094d\u092f\u093e \u091c\u093e\u0902\u091a\u0928\u093e \u091a\u093e\u0939\u0924\u0947 \u0939\u0948\u0902?",  // आप क्या जांचना चाहते हैं?
    kn: "\u0ca8\u0cc0\u0cb5\u0cc1 \u0c8f\u0ca8\u0ca8\u0ccd\u0ca8\u0cc1 \u0ca4\u0ca8\u0cbf\u0c96\u0cbf\u0cb8\u0cb2\u0cc1 \u0cac\u0caf\u0cb8\u0cc1\u0ca4\u0ccd\u0ca4\u0cc0\u0cb0\u0cbf?",  // ನೀವು ಏನನ್ನು ತನಿಖಿಸಲು ಬಯಸುತ್ತೀರಿ?
    mr: "\u0924\u0941\u092e\u094d\u0939\u093e\u0932\u093e \u0915\u093e\u092f \u0924\u092a\u093e\u0938\u093e\u092f\u091a\u0902 \u0906\u0939\u0947?",  // तुम्हाला काय तपासायचं आहे?
  },
  "home.needsAttention": {
    en: "Needs your attention",
    hi: "\u0906\u092a\u0915\u093e \u0927\u094d\u092f\u093e\u0928 \u091a\u093e\u0939\u093f\u090f",  // आपका ध्यान चाहिए
    kn: "\u0ca8\u0cbf\u092e\u094d\u092e \u0917\u092e\u0928 \u092c\u0947\u0915\u0941",  // ನಿಮ್ಮ ಗಮನ ಬೇಕು
    mr: "\u0924\u0941\u092e\u091a\u0947 \u0932\u0915\u094d\u0937 \u0939\u0935\u0947",  // तुमचे लक्ष हवे
  },
  "home.subtitle": {
    en: "Search a phone number, alias, vehicle, location, or any identifier. MUKTI will tell you what it already knows.",
    hi: "\u092b\u094b\u0928 \u0928\u0902\u092c\u0930, \u0909\u092a\u0928\u093e\u092e, \u0935\u093e\u0939\u0928, \u0938\u094d\u0925\u093e\u0928 \u092f\u093e \u0915\u094b\u0908 \u092d\u0940 \u092a\u0939\u091a\u093e\u0928 \u0916\u094b\u091c\u0947\u0902\u0964 \u092e\u0941\u0915\u094d\u0924\u093f \u0906\u092a\u0915\u094b \u092c\u0924\u093e\u090f\u0917\u0940 \u0915\u093f \u0909\u0938\u0947 \u092a\u0939\u0932\u0947 \u0938\u0947 \u0915\u094d\u092f\u093e \u092a\u0924\u093e \u0939\u0948\u0964",
    kn: "\u092b\u094b\u0928\u094d \u0938\u0902\u0916\u094d\u092f\u0947, \u0909\u092a\u0928\u093e\u092e, \u0935\u093e\u0939\u0928, \u0938\u094d\u0925\u0933 \u0905\u0925\u0935\u093e \u092f\u093e\u0935\u0941\u0926\u0947 \u0917\u0941\u0930\u0941\u0924\u0941 \u0939\u0941\u0921\u0941\u0915\u093f. \u092e\u0941\u0915\u094d\u0924\u093f \u0924\u0928\u0917\u0947 \u0906 \u092c\u0917\u094d\u0917\u0947 \u0917\u094a\u0924\u094d\u0924\u093f\u0930\u0941\u0935\u0941\u0926\u0928\u094d\u0928\u0941 \u0939\u0947\u0933\u0941\u0935\u0941\u0926\u0941.",
    mr: "\u092b\u094b\u0928 \u0915\u094d\u0930\u092e\u093e\u0902\u0915, \u091f\u094b\u092a\u0923\u0928\u093e\u0935, \u0935\u093e\u0939\u0928, \u0920\u093f\u0915\u093e\u0923 \u0915\u093f\u0902\u0935\u093e \u0915\u094b\u0923\u0924\u0940\u0939\u0940 \u0913\u0933\u0916 \u0936\u094b\u0927\u093e. \u092e\u0941\u0915\u094d\u0924\u0940\u0932\u093e \u0906\u0927\u0940\u091a \u091c\u0947 \u092e\u093e\u0939\u0940\u0924 \u0906\u0939\u0947 \u0924\u0947 \u0924\u0940 \u0938\u093e\u0902\u0917\u0947\u0932.",
  },
  "home.searching": {
    en: "Searching…",
    hi: "\u0916\u094b\u091c \u0930\u0939\u093e \u0939\u0948…",
    kn: "\u0cb9\u0cc1\u0ca1\u0cc1\u0c95\u0cbe\u091f\u0941\u0924\u094d\u0924\u093f\u0926\u0947…",
    mr: "\u0936\u094b\u0927\u0924 \u0906\u0939\u0947…",
  },
  "home.searchBtn": {
    en: "Search",
    hi: "\u0916\u094b\u091c\u0947\u0902",
    kn: "\u0cb9\u0cc1\u0ca1\u0cc1\u0c95\u093f",
    mr: "\u0936\u094b\u0927\u093e",
  },
  "leads.filter.all":    { en: "All",            hi: "\u0938\u092d\u0940",            kn: "\u0cb8\u0cb0\u094d\u0935",                            mr: "\u0938\u0930\u094d\u0935" },
  "leads.filter.high":   { en: "High attention", hi: "\u0909\u091a\u094d\u091a \u0927\u094d\u092f\u093e\u0928", kn: "\u0ca4\u0cc0\u0cb5\u094d\u0930 \u0917\u092e\u0928", mr: "\u0909\u091a\u094d\u091a \u0932\u0915\u094d\u0937" },
  "leads.filter.review": { en: "Review",         hi: "\u0938\u092e\u0940\u0915\u094d\u0937\u093e", kn: "\u0cb5\u093f\u092e\u0930\u094d\u0936\u0947",     mr: "\u092a\u0941\u0928\u0930\u094d\u0935\u093f\u091a\u093e\u0930" },
  "leads.filter.watch":  { en: "Watch",          hi: "\u0928\u093f\u0917\u0930\u093e\u0928\u0940",  kn: "\u0ca8\u093f\u0917\u093e\u0935\u0923\u0947",     mr: "\u0928\u093f\u0917\u0930\u093e\u0923\u0940" },
  "leads.rebuildBtn":    { en: "Rebuild",        hi: "\u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923",  kn: "\u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923",  mr: "\u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923" },
  "leads.rebuilding":    { en: "Rebuilding…",    hi: "\u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923 \u0939\u094b \u0930\u0939\u093e \u0939\u0948…",  kn: "\u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923\u0917\u094a\u0933\u094d\u0932\u0941\u0924\u094d\u0924\u093f\u0926\u0947…",  mr: "\u092a\u0941\u0928\u0903\u0928\u093f\u0930\u094d\u092e\u093e\u0923 \u0939\u094b\u0924 \u0906\u0939\u0947…" },
};

const KEY = "mukti.locale";

const LocaleContext = createContext({ locale: "en", setLocale: () => {}, t: (k) => k });

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try { return localStorage.getItem(KEY) || "en"; } catch { return "en"; }
  });
  const setLocale = useCallback((code) => {
    setLocaleState(code);
    try { localStorage.setItem(KEY, code); } catch {
      // localStorage disabled (private mode) — in-memory only is fine.
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const t = useCallback((key) => {
    const entry = dict[key];
    if (!entry) return key;
    return entry[locale] || entry.en || key;
  }, [locale]);
  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
